import { VariantProps, tv } from './cn';

const textContentColors = tv({
  variants: {
    color: {
      primary: 'text-text-primary',
      secondary: 'text-text-secondary',
      tertiary: 'text-text-tertiary',
      supporting: 'text-text-supporting',
      disabled: 'text-text-disabled',
      inherit: 'text-inherit',
    },
  },
  defaultVariants: {
    color: 'primary',
  },
});

const headingTextStyles = tv({
  extend: textContentColors,
  base: '',
  variants: {
    responsive: {
      true: '',
      false: '',
    },
    size: {
      '3xl': 'text-[2.250rem] leading-[3.000rem]', // 36px - 48px
      '2xl': 'text-[2.000rem] leading-[2.625rem]', // 32px - 42px
      xl: 'text-[1.750rem] leading-[2.250rem]', // 28px - 36px
      lg: 'text-[1.500rem] leading-[2.000rem]', // 24px - 32px
      md: 'text-[1.250rem] leading-[1.625rem]', // 20px - 26px
      sm: 'text-[1.125rem] leading-[1.500rem]', // 18px - 24px
    },
    weight: {
      bold: 'font-bold', // 700
      semibold: 'font-semibold', // 600
      medium: 'font-medium', // 500
    },
  },
  compoundVariants: [
    {
      responsive: true,
      size: '3xl',
      className:
        'text-[2.000rem] leading-[2.625rem] @md:text-[2.250rem] @md:leading-[3.000rem]',
    },
    {
      responsive: true,
      size: '2xl',
      className:
        'text-[1.750rem] leading-[2.250rem] @md:text-[2.000rem] @md:leading-[2.625rem]',
    },
    {
      responsive: true,
      size: 'xl',
      className:
        'text-[1.500rem] leading-[2.000rem] @md:text-[1.750rem] @md:leading-[2.250rem]',
    },
    {
      responsive: true,
      size: 'lg',
      className:
        'text-[1.250rem] leading-[1.625rem] @md:text-[1.500rem] @md:leading-[2.000rem]',
    },
    {
      responsive: true,
      size: 'md',
      className:
        'text-[1.125rem] leading-[1.500rem] @md:text-[1.250rem] @md:leading-[1.625rem]',
    },
    {
      responsive: true,
      size: 'sm',
      className:
        'text-[1.000rem] leading-[1.375rem] @md:text-[1.125rem] @md:leading-[1.500rem]',
    },
  ],
  defaultVariants: {
    responsive: false,
    size: '3xl',
    weight: 'medium',
  },
});

export type HeadingTextStylesProps = VariantProps<typeof headingTextStyles>;

const bodyTextStyles = tv({
  extend: textContentColors,
  base: 'tracking-[-0.01em]',
  variants: {
    responsive: {
      true: '',
      false: '',
    },
    size: {
      lg: 'text-[1.000rem] leading-[1.500rem]', // 16px - 24px
      md: 'text-[0.875rem] leading-[1.250rem]', // 14px - 20px
      sm: 'text-[0.750rem] leading-[1.125rem] tracking-normal', // 12px - 18px
    },
    weight: {
      bold: 'font-bold', // 700
      semibold: 'font-semibold', // 600
      medium: 'font-medium', // 500
      regular: 'font-normal', // 400
    },
  },
  compoundVariants: [
    {
      responsive: true,
      size: 'lg',
      className:
        'text-[0.875rem] leading-[1.250rem] @md:text-[1.000rem] @md:leading-[1.500rem]',
    },
    {
      responsive: true,
      size: 'md',
      className:
        'text-[0.750rem] leading-[1.125rem] @md:text-[0.875rem] @md:leading-[1.250rem]',
    },
    {
      responsive: true,
      size: 'sm',
      className:
        'text-[0.6875rem] leading-[1.000rem] @md:text-[0.750rem] @md:leading-[1.125rem]',
    },
  ],
  defaultVariants: {
    responsive: false,
    size: 'lg',
    weight: 'medium',
  },
});

export type BodyTextStylesProps = VariantProps<typeof bodyTextStyles>;

const captionTextStyles = tv({
  extend: textContentColors,
  base: 'uppercase tracking-[0.01em]',
  variants: {
    size: {
      md: 'text-[0.6875rem] leading-[1.000rem]', // 11px - 16px
      sm: 'text-[0.625rem] leading-[0.875rem]', // 10px - 14px
    },
    weight: {
      bold: 'font-bold', // 700
      semibold: 'font-semibold', // 600
    },
  },
  defaultVariants: {
    weight: 'semibold',
    size: 'md',
  },
});

export type CaptionTextStylesProps = VariantProps<typeof captionTextStyles>;

export const textStyles = {
  heading: headingTextStyles,
  body: bodyTextStyles,
  caption: captionTextStyles,
};
